/**
 * MoveGizmo — Fusion-style Move/Rotate handles on the selected Transform
 * feature: three axis arrows (translate), three rings (rotate about the
 * pivot), and free-move by dragging the body in the view plane.
 *
 * SketchUp semantics on top: hold ⌥/Alt while dragging to MOVE-COPY
 * (original stays, a copy moves), and after releasing, type "5x" to array
 * five copies along the drag — the host arms a post-gesture edit window.
 * All edits go through the document preview channel; one undo per drag.
 */

import * as THREE from 'three/webgpu';

import type { CadDocument } from '@core/document';
import {
  collectEdgeSnaps3D,
  type SnapPoint3,
  type SnapSegment3,
} from '@core/geometry/snap';
import type { FeatureId, FeatureNode, TransformParams } from '@core/timeline/types';
import type { DragHandler } from '@render/cadControls';
import type { Viewport } from '@render/viewport';
import { ensureNormals } from '@render/meshView';
import type { DimInput } from '@ui/dimInput';
import { SNAP_COLORS, SNAP_LABELS } from '@ui/snapTheme';

const SNAP_PX = 10; // screen-space snap radius while moving

type V3 = [number, number, number];
export type PostGesture = {
  featureId: FeatureId;
  kind: 'move' | 'rotate';
  /** Unit direction of the last move (for exact typed distances). */
  dir: V3;
  clientX: number;
  clientY: number;
};

const AXIS_COLORS = [0xc4504e, 0x59a869, 0x4c8dd6] as const; // X Y Z
const AXES: V3[] = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
];

export class MoveGizmo {
  private group = new THREE.Group();
  private arrows: { mesh: THREE.Object3D; axis: 0 | 1 | 2 }[] = [];
  private rings: { mesh: THREE.Mesh; axis: 0 | 1 | 2 }[] = [];
  private featureId: FeatureId | null = null;
  private pivot = new THREE.Vector3();

  private snapMarker: THREE.Mesh | null = null;
  private snapBadge: HTMLDivElement;

  constructor(
    private readonly viewport: Viewport,
    private readonly host: {
      readonly doc: CadDocument;
      armPostGesture(pg: PostGesture | null): void;
    },
    private readonly dim?: DimInput,
  ) {
    this.group.renderOrder = 999;
    viewport.gizmos.add(this.group);
    this.snapBadge = document.createElement('div');
    this.snapBadge.className = 'nv-snap-badge';
    this.snapBadge.style.display = 'none';
    viewport.element.appendChild(this.snapBadge);
  }

  // --- 3D snapping ------------------------------------------------------------

  /** World snap candidates from every visible body EXCEPT the moving one. */
  private buildWorldSnaps(excludeId: FeatureId): {
    points: SnapPoint3[];
    segments: SnapSegment3[];
  } {
    const points: SnapPoint3[] = [];
    const segments: SnapSegment3[] = [];
    const report = this.host.doc.lastReport;
    if (!report) return { points, segments };
    for (const [id, result] of report.results) {
      if (id === excludeId || result.kind !== 'body' || report.consumed.has(id)) continue;
      if (result.mesh.edgePositions.length === 0) continue;
      const c = collectEdgeSnaps3D(result.mesh.edgePositions, result.mesh.vertexPositions);
      points.push(...c.points);
      segments.push(...c.segments);
    }
    return { points, segments };
  }

  private toScreen(p: THREE.Vector3): { x: number; y: number } {
    const rect = this.viewport.element.getBoundingClientRect();
    const v = p.clone().project(this.viewport.camera);
    return {
      x: rect.left + ((v.x + 1) / 2) * rect.width,
      y: rect.top + ((1 - v.y) / 2) * rect.height,
    };
  }

  /** Nearest candidate to the pointer in SCREEN space (points beat edges). */
  private resolveSnap3D(
    e: PointerEvent,
    points: readonly SnapPoint3[],
    segments: readonly SnapSegment3[],
  ): { p: THREE.Vector3; kind: 'endpoint' | 'midpoint' | 'on-edge' } | null {
    let best: { p: THREE.Vector3; kind: 'endpoint' | 'midpoint' | 'on-edge' } | null = null;
    let bestD = SNAP_PX;
    for (const c of points) {
      const s = this.toScreen(new THREE.Vector3(...c.p));
      const d = Math.hypot(e.clientX - s.x, e.clientY - s.y);
      if (d < bestD) {
        bestD = d;
        best = { p: new THREE.Vector3(...c.p), kind: c.kind };
      }
    }
    if (best) return best;
    const ray = this.pointerRay(e);
    let bestSegD = SNAP_PX * 0.85;
    for (const s of segments) {
      const onSeg = new THREE.Vector3();
      ray.ray.distanceSqToSegment(
        new THREE.Vector3(...s.a),
        new THREE.Vector3(...s.b),
        undefined,
        onSeg,
      );
      const sc = this.toScreen(onSeg);
      const d = Math.hypot(e.clientX - sc.x, e.clientY - sc.y);
      if (d < bestSegD) {
        bestSegD = d;
        best = { p: onSeg.clone(), kind: 'on-edge' };
      }
    }
    return best;
  }

  private showSnapFeedback(
    snap: { p: THREE.Vector3; kind: 'endpoint' | 'midpoint' | 'on-edge' } | null,
    e: PointerEvent,
  ): void {
    if (this.snapMarker) {
      this.viewport.gizmos.remove(this.snapMarker);
      this.snapMarker = null;
    }
    if (!snap) {
      this.snapBadge.style.display = 'none';
      return;
    }
    const dist = snap.p.distanceTo(this.viewport.camera.position);
    const radius = (dist * Math.tan((this.viewport.camera.fov * Math.PI) / 360) * 2 * 4) /
      (this.viewport.element.clientHeight || 1); // ~4px world radius
    this.snapMarker = new THREE.Mesh(
      new THREE.SphereGeometry(Math.max(radius, 0.3), 10, 10),
      new THREE.MeshBasicMaterial({ color: SNAP_COLORS[snap.kind] ?? 0xffffff, depthTest: false }),
    );
    this.snapMarker.position.copy(snap.p);
    this.snapMarker.renderOrder = 1002;
    this.viewport.gizmos.add(this.snapMarker);
    const rect = this.viewport.element.getBoundingClientRect();
    this.snapBadge.textContent = SNAP_LABELS[snap.kind] ?? '';
    this.snapBadge.style.display = 'block';
    this.snapBadge.style.left = `${e.clientX - rect.left + 14}px`;
    this.snapBadge.style.top = `${e.clientY - rect.top - 26}px`;
    this.viewport.requestRender();
  }

  sync(id: FeatureId | null): void {
    const doc = this.host.doc;
    const feature = id ? doc.timeline.get(id) : undefined;
    const result = id ? doc.lastReport?.results.get(id) : undefined;
    if (!feature || (feature.type !== 'transform' && feature.type !== 'instance') || result?.kind !== 'body' ||
        result.mesh.positions.length === 0) {
      this.featureId = null;
      this.group.clear();
      this.arrows = [];
      this.rings = [];
      this.viewport.requestRender();
      return;
    }
    this.featureId = id;
    this.rebuild(result.mesh.positions, feature.params as TransformParams);
  }

  private rebuild(positions: Float32Array, params: TransformParams): void {
    this.group.clear();
    this.arrows = [];
    this.rings = [];

    // Gizmo sits at the FIRST instance's current center: pivot + translation.
    this.pivot.set(
      params.rotationCenter[0] + params.translation[0],
      params.rotationCenter[1] + params.translation[1],
      params.rotationCenter[2] + params.translation[2],
    );
    let diag = 1;
    {
      const min = [Infinity, Infinity, Infinity];
      const max = [-Infinity, -Infinity, -Infinity];
      for (let i = 0; i < positions.length; i += 3) {
        for (let k = 0; k < 3; k++) {
          const v = positions[i + k]!;
          if (v < min[k]!) min[k] = v;
          if (v > max[k]!) max[k] = v;
        }
      }
      diag = Math.hypot(max[0]! - min[0]!, max[1]! - min[1]!, max[2]! - min[2]!) || 1;
    }
    const L = Math.max(diag * 0.45, 12);

    for (const axis of [0, 1, 2] as const) {
      const color = AXIS_COLORS[axis];
      const mat = new THREE.MeshBasicMaterial({ color, depthTest: false });
      const arrow = new THREE.Group();
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(L * 0.02, L * 0.02, L, 8), mat);
      shaft.position.y = L / 2;
      const tip = new THREE.Mesh(new THREE.ConeGeometry(L * 0.07, L * 0.18, 12), mat);
      tip.position.y = L + L * 0.09;
      arrow.add(shaft, tip);
      // Cylinder runs along +Y; orient to the axis.
      if (axis === 0) arrow.rotation.z = -Math.PI / 2;
      if (axis === 2) arrow.rotation.x = Math.PI / 2;
      arrow.position.copy(this.pivot);
      arrow.renderOrder = 1000;
      this.group.add(arrow);
      this.arrows.push({ mesh: arrow, axis });

      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(L * 0.75, L * 0.015, 6, 64),
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.6,
          depthTest: false,
        }),
      );
      // Fat invisible companion so the razor-thin ring is grabbable.
      const hitRing = new THREE.Mesh(
        new THREE.TorusGeometry(L * 0.75, L * 0.09, 6, 48),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
      );
      for (const r of [ring, hitRing]) {
        // Torus lies in XY (around Z); orient to encircle the axis.
        if (axis === 0) r.rotation.y = Math.PI / 2;
        if (axis === 1) r.rotation.x = Math.PI / 2;
        r.position.copy(this.pivot);
        r.renderOrder = 999;
        ensureNormals(r.geometry);
        this.group.add(r);
      }
      this.rings.push({ mesh: hitRing, axis });
    }
    this.viewport.requestRender();
  }

  /** CADControls.onDragStart hook — runs before body push/pull. */
  tryStart = (e: PointerEvent): DragHandler | null => {
    if (!this.featureId) return null;
    const doc = this.host.doc;
    const feature = doc.timeline.get(this.featureId) as FeatureNode<'transform' | 'instance'> | undefined;
    if (!feature) return null;
    const params = feature.params as TransformParams;
    const ray = this.pointerRay(e);

    const ringHit = ray.intersectObjects(this.rings.map((r) => r.mesh), false)[0];
    if (ringHit) {
      const axis = this.rings.find((r) => r.mesh === ringHit.object)!.axis;
      return this.startRotate(feature, params, axis, e);
    }
    const arrowHit = ray.intersectObjects(this.arrows.map((a) => a.mesh), true)[0];
    if (arrowHit) {
      let node: THREE.Object3D | null = arrowHit.object;
      while (node && !this.arrows.some((a) => a.mesh === node)) node = node.parent;
      const axis = this.arrows.find((a) => a.mesh === node)!.axis;
      return this.startMove(feature, params, AXES[axis]!, e);
    }
    // Dragging the transform's own body: free-move with 3D snapping. Let
    // Alt/Shift+left fall through so the universal orbit (Alt) and additive
    // marquee (Shift) gestures still work over the selected body.
    const bodyHit = this.viewport.raycastBodies(e.clientX, e.clientY);
    if (bodyHit && bodyHit.featureId === this.featureId && !e.altKey && !e.shiftKey) {
      return this.startBodyMove(feature, params, bodyHit.point, e);
    }
    return null;
  };

  // --- move (axis + planar) ---------------------------------------------------

  private startMove(
    feature: FeatureNode<'transform' | 'instance'>,
    params: TransformParams,
    axis: V3,
    e: PointerEvent,
  ): DragHandler {
    const axisV = new THREE.Vector3(...axis);
    const t0 = this.axisParam(axisV, this.pivot.clone(), e);
    return this.moveHandler(feature, params, (ev) => {
      const d = Math.round((this.axisParam(axisV, this.pivot.clone(), ev) - t0) * 2) / 2;
      return [axis[0] * d, axis[1] * d, axis[2] * d];
    });
  }

  /**
   * SketchUp-style body move: the press GRABS the body at its nearest
   * corner/midpoint (within a small screen radius), and the drag target
   * snaps to other bodies' endpoints/midpoints/edges — so "pick it up by
   * this corner, put that corner on that edge" works exactly. No snap →
   * smooth view-plane move.
   */
  private startBodyMove(
    feature: FeatureNode<'transform' | 'instance'>,
    params: TransformParams,
    hitPoint: THREE.Vector3,
    e: PointerEvent,
  ): DragHandler | null {
    const doc = this.host.doc;
    // Grab point: prefer the moving body's own feature points near the press.
    const grab = hitPoint.clone();
    const own = doc.lastReport?.results.get(feature.id);
    if (own?.kind === 'body' && own.mesh.edgePositions.length > 0) {
      const c = collectEdgeSnaps3D(own.mesh.edgePositions, own.mesh.vertexPositions);
      const hs = this.toScreen(hitPoint);
      let bestD = SNAP_PX * 1.5;
      for (const pt of c.points) {
        const s = this.toScreen(new THREE.Vector3(...pt.p));
        const d = Math.hypot(hs.x - s.x, hs.y - s.y);
        if (d < bestD) {
          bestD = d;
          grab.set(...pt.p);
        }
      }
    }
    const targets = this.buildWorldSnaps(feature.id);

    const viewDir = this.viewport.camera.getWorldDirection(new THREE.Vector3());
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(viewDir, grab);
    const p0 = this.pointerRay(e).ray.intersectPlane(plane, new THREE.Vector3());
    if (!p0) return null;

    const base = doc.timeline;
    const baseT = params.translation;
    const baseCopies = params.copies;
    let lastPreview: Promise<unknown> = Promise.resolve();
    let lastDelta: V3 = [0, 0, 0];
    let lastEv: PointerEvent | null = null;

    return {
      move: (ev: PointerEvent): void => {
        lastEv = ev;
        const snap = this.resolveSnap3D(ev, targets.points, targets.segments);
        let d: V3;
        if (snap) {
          // Land the grab point exactly on the snap target.
          d = [snap.p.x - grab.x, snap.p.y - grab.y, snap.p.z - grab.z];
        } else {
          const p = this.pointerRay(ev).ray.intersectPlane(plane, new THREE.Vector3());
          if (!p) return;
          const delta = p.sub(p0);
          d = [
            Math.round(delta.x * 2) / 2,
            Math.round(delta.y * 2) / 2,
            Math.round(delta.z * 2) / 2,
          ];
        }
        lastDelta = d;
        this.showSnapFeedback(snap, ev);
        const copies = baseCopies > 0 ? baseCopies : ev.altKey ? 1 : 0;
        const dist = Math.hypot(...d);
        this.dim?.show(
          `${dist.toFixed(1)} mm` +
            (copies > 0 ? ` · copy${copies > 1 ? ` ×${copies}` : ''}` : '') +
            (snap ? ` · ${SNAP_LABELS[snap.kind]}` : ''),
          ev.clientX,
          ev.clientY,
          null,
        );
        lastPreview = doc.preview(
          base.updateFeature(feature.id, {
            ...params,
            translation: [baseT[0] + d[0], baseT[1] + d[1], baseT[2] + d[2]],
            copies,
          }),
        );
      },
      up: (): void => {
        this.dim?.hide();
        this.showSnapFeedback(null, e);
        const len = Math.hypot(...lastDelta) || 1;
        this.host.armPostGesture(
          lastEv
            ? {
                featureId: feature.id,
                kind: 'move',
                dir: [lastDelta[0] / len, lastDelta[1] / len, lastDelta[2] / len],
                clientX: lastEv.clientX,
                clientY: lastEv.clientY,
              }
            : null,
        );
        void lastPreview.then(() => doc.commitPreview(`Move ${feature.name}`));
      },
    };
  }

  private moveHandler(
    feature: FeatureNode<'transform' | 'instance'>,
    params: TransformParams,
    deltaOf: (ev: PointerEvent) => V3,
  ): DragHandler {
    const doc = this.host.doc;
    const base = doc.timeline;
    const baseT = params.translation;
    const baseCopies = params.copies;
    let lastPreview: Promise<unknown> = Promise.resolve();
    let lastDelta: V3 = [0, 0, 0];
    let lastEv: PointerEvent | null = null;

    return {
      move: (ev: PointerEvent): void => {
        lastEv = ev;
        const d = deltaOf(ev);
        lastDelta = d;
        // ⌥ while dragging = move-copy (only when not already an array).
        const copies = baseCopies > 0 ? baseCopies : ev.altKey ? 1 : 0;
        const dist = Math.hypot(...d);
        this.dim?.show(
          `${dist.toFixed(1)} mm${copies > 0 ? ` · copy${copies > 1 ? ` ×${copies}` : ''}` : ''}`,
          ev.clientX,
          ev.clientY,
          null,
        );
        lastPreview = doc.preview(
          base.updateFeature(feature.id, {
            ...params,
            translation: [baseT[0] + d[0], baseT[1] + d[1], baseT[2] + d[2]],
            copies,
          }),
        );
      },
      up: (): void => {
        this.dim?.hide();
        const len = Math.hypot(...lastDelta) || 1;
        this.host.armPostGesture(
          lastEv
            ? {
                featureId: feature.id,
                kind: 'move',
                dir: [lastDelta[0] / len, lastDelta[1] / len, lastDelta[2] / len],
                clientX: lastEv.clientX,
                clientY: lastEv.clientY,
              }
            : null,
        );
        void lastPreview.then(() => doc.commitPreview(`Move ${feature.name}`));
      },
    };
  }

  // --- rotate -------------------------------------------------------------------

  private startRotate(
    feature: FeatureNode<'transform' | 'instance'>,
    params: TransformParams,
    axis: 0 | 1 | 2,
    e: PointerEvent,
  ): DragHandler | null {
    const doc = this.host.doc;
    const axisV = new THREE.Vector3(...AXES[axis]!);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(axisV, this.pivot);
    const start = this.pointerRay(e).ray.intersectPlane(plane, new THREE.Vector3());
    if (!start) return null;
    const v0 = start.sub(this.pivot);
    if (v0.length() < 1e-6) return null;

    const axisName = (['X', 'Y', 'Z'] as const)[axis]!;
    // Rotating about a NEW axis restarts the angle; same axis accumulates.
    const baseAngle = params.rotationAxis === axisName ? params.rotationAngle : 0;
    const baseCopies = params.copies;
    const base = doc.timeline;
    let lastPreview: Promise<unknown> = Promise.resolve();
    let lastEv: PointerEvent | null = null;

    return {
      move: (ev: PointerEvent): void => {
        lastEv = ev;
        const p = this.pointerRay(ev).ray.intersectPlane(plane, new THREE.Vector3());
        if (!p) return;
        const v1 = p.sub(this.pivot);
        if (v1.length() < 1e-6) return;
        let delta = Math.atan2(
          new THREE.Vector3().crossVectors(v0, v1).dot(axisV),
          v0.dot(v1),
        );
        delta = (Math.round((delta * 180) / Math.PI) * Math.PI) / 180; // 1° snap
        const copies = baseCopies > 0 ? baseCopies : ev.altKey ? 1 : 0;
        const deg = Math.round(((baseAngle + delta) * 180) / Math.PI);
        this.dim?.show(
          `${deg}°${copies > 0 ? ` · copy${copies > 1 ? ` ×${copies}` : ''}` : ''}`,
          ev.clientX,
          ev.clientY,
          null,
        );
        lastPreview = doc.preview(
          base.updateFeature(feature.id, {
            ...params,
            rotationAxis: axisName,
            rotationAngle: baseAngle + delta,
            copies,
          }),
        );
      },
      up: (): void => {
        this.dim?.hide();
        this.host.armPostGesture(
          lastEv
            ? {
                featureId: feature.id,
                kind: 'rotate',
                dir: AXES[axis]!,
                clientX: lastEv.clientX,
                clientY: lastEv.clientY,
              }
            : null,
        );
        void lastPreview.then(() => doc.commitPreview(`Rotate ${feature.name}`));
      },
    };
  }

  // --- plumbing -------------------------------------------------------------------

  private pointerRay(e: PointerEvent): THREE.Raycaster {
    const rect = this.viewport.element.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.viewport.camera);
    return ray;
  }

  private axisParam(axis: THREE.Vector3, origin: THREE.Vector3, e: PointerEvent): number {
    const ray = this.pointerRay(e);
    const a = origin.clone().addScaledVector(axis, -1e5);
    const b = origin.clone().addScaledVector(axis, 1e5);
    const onSegment = new THREE.Vector3();
    ray.ray.distanceSqToSegment(a, b, undefined, onSegment);
    return onSegment.sub(origin).dot(axis);
  }
}
